import { Modal, Button, Input, Space, Typography, Checkbox } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { useMainContext } from '../../hooks/useMainContext';
import { message } from 'antd';
import { TbX, TbArrowsMove } from 'react-icons/tb';
import Draggable from 'react-draggable';
import {
    DEFAULT_PAGE_SCALE,
    EXTRACT_TEXT_HEIGHT_PERCENTAGE,
    EXTRACT_TEXT_WIDTH_PERCENTAGE,
} from '../../constant';
import { autorun } from 'mobx';
import CustomInput from '../Globle/CustomInput';
import { Utils } from '../../utils/Utils';

export const UnitForm = observer(() => {
    const { closetManager, viewManager } = useMainContext();
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [scaleValue, setScaleValue] = useState();
    // State to track if the scale is set to default
    const [isDefault, setIsDefault] = useState(true);

    useEffect(() => {
        const dispose = autorun(() => {
            const newScale = closetManager.getScaleForUnit(
                closetManager.selectedUnit?.id,
            );
            if (!newScale) {
                setScaleValue(DEFAULT_PAGE_SCALE);
                return;
            }
            setScaleValue(newScale);
        });

        return () => dispose();
    }, []);
    useEffect(() => {
        if (viewManager.unitForm) {
            setModalPosition({ x: 0, y: 0 });
        }
    }, [viewManager.unitForm]);
    const selectedUnit = closetManager.selectedUnit;

    // State for editing unit name and quantity (only updates on Save click)
    const [newUnitName, setNewUnitName] = useState('');
    const [newQty, setNewQty] = useState(1);
    const handleDefaultScale = async () => {
        const page = await closetManager.pdfDoc.getPage(
            closetManager.selectedPageNo,
        );
        const newScale = await Utils.extractTextFromCustomBox(
            EXTRACT_TEXT_WIDTH_PERCENTAGE,
            EXTRACT_TEXT_HEIGHT_PERCENTAGE,
            page,
        );
        if (!newScale) return;
        if (newScale === selectedUnit.scaleOnPdf) {
            setIsDefault(true);
            return;
        }
        setIsDefault(false);
    };
    // Initialize state when modal opens
    useEffect(() => {
        if (selectedUnit) {
            setNewUnitName(selectedUnit.name);
            setScaleValue(selectedUnit.scaleOnPdf);
            setNewQty(selectedUnit.qty);
            handleDefaultScale();
        }
    }, [selectedUnit]);

    const saveChanges = () => {
        if (!newUnitName.trim()) {
            message.warning('Unit name cannot be empty.');
            return;
        }
        const parsedValue = Number(scaleValue);
        if (isNaN(parsedValue) || parsedValue <= 0) {
            message.error('Scale must be a positive number');
            setScaleValue(
                closetManager.getScaleForUnit(closetManager.selectedUnit.id),
            );
            return;
        }

        closetManager.setUnitScale(closetManager.selectedUnit.id, parsedValue);
        closetManager.drawNewClosets();
        if (
            newUnitName !== selectedUnit._name &&
            closetManager.units.some(
                (u) =>
                    u._name === newUnitName &&
                    u._pageNo === selectedUnit._pageNo,
            )
        ) {
            message.error('A unit with this name already exists.');
            return;
        }

        // Update Unit Name if changed
        if (newUnitName !== selectedUnit._name) {
            closetManager.updateUnitNameByName(selectedUnit.name, newUnitName);
        }

        // Update Quantity if changed
        if (newQty !== selectedUnit._qty) {
            closetManager.updateUnitQtyByName(newUnitName, newQty);
        }

        message.success('Unit updated successfully.');
        viewManager.setUnitForm(false);
    };

    const closeModal = () => {
        // Reset the fields to original values when closing
        if (selectedUnit) {
            setNewUnitName(selectedUnit._name);
            setNewQty(selectedUnit._qty);
        }
        if (
            scaleValue !==
            closetManager.getScaleForUnit(closetManager.selectedUnit.id)
        ) {
            setScaleValue(
                closetManager.getScaleForUnit(closetManager.selectedUnit.id),
            );
        }
        viewManager.setUnitForm(false);
    };
    return (
        <Modal
            className="unit-form-modal"
            width={500}
            closable={false}
            footer={null}
            styles={{
                content: {
                    padding: '32px',
                    borderRadius: '32px',
                },
            }}
            centered
            open={viewManager.unitForm}
            onCancel={closeModal}
            modalRender={(modal) => (
                <Draggable
                    handle=".drag-button"
                    cancel=".ant-input, .ant-btn:not(.drag-button)"
                    onStop={(_, data) => {
                        setModalPosition({ x: data.x, y: data.y });
                    }}
                    position={modalPosition}>
                    <div>{modal}</div>
                </Draggable>
            )}>
            {/* Header */}
            <div
                className="border-b-[1px] border-theme-border pb-2"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                }}>
                <Typography.Title
                    level={4}
                    style={{
                        margin: 0,
                        fontSize: '32px',
                        lineHeight: '40px',
                        color: '#000000',
                    }}>
                    Edit Unit
                </Typography.Title>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        className="drag-button flex items-center justify-center p-0 w-[32px] h-[32px]"
                        style={{ cursor: 'move', border: 'none' }}>
                        <TbArrowsMove size={20} />{' '}
                    </Button>

                    <Button
                        type="text"
                        className="flex items-center justify-center p-0 w-[32px] h-[32px]"
                        onClick={closeModal}>
                        <TbX size={20} />
                    </Button>
                </div>
            </div>

            {selectedUnit ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 15,
                    }}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}>
                        <Typography.Text className="custom-label">
                            Unit Name
                        </Typography.Text>

                        <CustomInput
                            value={newUnitName}
                            onChange={(e) => {
                                const value = e.target.value;
                                const regex =
                                    /^[a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{0,24}$/;
                                if (regex.test(value)) {
                                    setNewUnitName(value);
                                }
                            }}
                            placeholder="Unit"
                            maxLength={24}
                        />
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}>
                        <div className="flex justify-between items-center">
                            <Typography.Text className="custom-label">
                                Unit Scale
                            </Typography.Text>
                            <div className="flex gap-[4px] items-center ">
                                <Checkbox
                                    checked={isDefault}
                                    onChange={async (e) => {
                                        e.stopPropagation();
                                        setIsDefault(e.target.checked);
                                        if (e.target.checked) {
                                            const page =
                                                await closetManager.pdfDoc.getPage(
                                                    closetManager.selectedPageNo,
                                                );
                                            const newScale =
                                                await Utils.extractTextFromCustomBox(
                                                    EXTRACT_TEXT_WIDTH_PERCENTAGE,
                                                    EXTRACT_TEXT_HEIGHT_PERCENTAGE,
                                                    page,
                                                );
                                            setScaleValue(newScale);
                                        }
                                    }}
                                    className="custom-checkbox"
                                />
                                <p className="font-favorit text-[14px] font-[400] leading-[24px] mt-[2px] text-theme-black ">
                                    Default
                                </p>
                            </div>
                        </div>
                        <CustomInput
                            disabled={isDefault}
                            value={scaleValue}
                            onChange={(e) => setScaleValue(e.target.value)}
                            placeholder="Scale"
                        />
                    </div>
                </div>
            ) : (
                <Typography.Text type="secondary">
                    No unit selected.
                </Typography.Text>
            )}

            <div
                style={{
                    marginTop: 20,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 10,
                }}>
                <Button
                    type="primary"
                    className="bg-theme-orange text-theme-white !rounded-[36px] text-[16px] w-[140px] h-[40px] !!hover:bg-theme-orange-hover !shadow-none save-changes-button"
                    onClick={saveChanges}
                    disabled={!selectedUnit}>
                    Confirm
                </Button>
                {/* <Button
                    onClick={closeModal}
                    variant="primary"
                    className="bg-theme-button-bg close-button">
                    Close
                </Button> */}
            </div>
        </Modal>
    );
});
